import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { listScenarios, compareScenarios, compareCashflows, deleteScenario, fmt, fmtPct, fmtFull } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { GitCompare, Trash2, X, TrendingUp, BarChart3 } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";

const COLORS = ["#0055FF", "#059669", "#D97706", "#DC2626", "#7C3AED", "#0891B2", "#BE185D"];
const DEBT_COLORS = ["#0055FF80", "#05966980", "#D9770680", "#DC262680", "#7C3AED80", "#0891B280", "#BE185D80"];

export default function Compare() {
  const [scenarios, setScenarios] = useState([]);
  const [selected, setSelected] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [cashflowComparison, setCashflowComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cfLoading, setCfLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    listScenarios().then((data) => {
      setScenarios(data);
      const idsParam = searchParams.get("ids");
      if (idsParam) {
        const ids = idsParam.split(",").filter(Boolean);
        const valid = ids.filter((id) => data.some((s) => s.id === id));
        if (valid.length >= 2) {
          setSelected(valid);
          // Auto-trigger comparison
          setLoading(true);
          compareScenarios(valid).then(setComparison).catch(() => toast.error("Failed to load comparison")).finally(() => setLoading(false));
        }
      }
    }).catch(console.error);
  }, [searchParams]);

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const runCompare = async () => {
    if (selected.length < 2) {
      toast.error("Select at least 2 scenarios to compare");
      return;
    }
    setLoading(true);
    try {
      const data = await compareScenarios(selected);
      setComparison(data);
    } catch (e) {
      toast.error("Failed to load comparison");
    } finally {
      setLoading(false);
    }
  };

  const loadCashflowOverlay = async () => {
    if (cashflowComparison) return; // already loaded
    setCfLoading(true);
    try {
      const data = await compareCashflows(selected);
      setCashflowComparison(data);
    } catch (e) {
      toast.error("Failed to load cashflow comparison");
    } finally {
      setCfLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteScenario(id);
      setScenarios((p) => p.filter((s) => s.id !== id));
      setSelected((p) => p.filter((s) => s !== id));
      toast.success("Scenario deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const handleBack = () => {
    setComparison(null);
    setCashflowComparison(null);
  };

  const metrics = [
    { label: "Total Units", get: (s) => s.results?.kpis?.total_units },
    { label: "Total NIA (sq ft)", get: (s) => s.results?.kpis?.total_nia?.toLocaleString("en-GB") },
    { label: "Gross Dev Value", get: (s) => fmtFull(s.results?.kpis?.total_gdv) },
    { label: "Net Dev Value", get: (s) => fmtFull(s.results?.kpis?.total_ndv) },
    { label: "Total Costs", get: (s) => fmtFull(s.results?.kpis?.total_costs) },
    { label: "Dev Profit", get: (s) => fmtFull(s.results?.kpis?.dev_profit) },
    { label: "Profit on Cost", get: (s) => fmtPct(s.results?.kpis?.dev_profit_on_cost) },
    { label: "Ungeared IRR", get: (s) => fmtPct(s.results?.kpis?.ungeared_irr) },
    { label: "Geared IRR", get: (s) => fmtPct(s.results?.kpis?.geared_irr) },
    { label: "Ungeared Multiple", get: (s) => s.results?.kpis?.ungeared_multiple?.toFixed(2) + "x" },
    { label: "Geared Multiple", get: (s) => s.results?.kpis?.geared_multiple?.toFixed(2) + "x" },
    { label: "Loan Amount", get: (s) => fmtFull(s.results?.kpis?.loan_amount) },
    { label: "Total Finance", get: (s) => fmtFull(s.results?.kpis?.total_finance) },
  ];

  // Merge cashflow data for overlay chart
  const buildOverlayData = () => {
    if (!cashflowComparison || cashflowComparison.length === 0) return [];
    const maxMonth = Math.max(...cashflowComparison.map((s) => s.cashflow.length));
    const merged = [];
    for (let m = 0; m < maxMonth; m++) {
      const point = { month: m };
      cashflowComparison.forEach((s, i) => {
        const row = s.cashflow[m];
        if (row) {
          point[`cum_${i}`] = row.cumulative;
          point[`debt_${i}`] = row.debt_balance;
        }
      });
      merged.push(point);
    }
    return merged;
  };

  return (
    <div className="page-enter" style={{ padding: "32px 40px" }}>
      <h1 style={{ fontFamily: "Manrope", fontWeight: 800, fontSize: 28, marginBottom: 8 }} data-testid="compare-title">
        Compare Scenarios
      </h1>
      <p style={{ color: "hsl(var(--muted-foreground))", fontSize: 14, marginBottom: 24 }}>
        Select scenarios to compare side by side
      </p>

      {!comparison ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12, marginBottom: 24 }}>
            {scenarios.map((s) => (
              <div
                key={s.id}
                className="scenario-card"
                style={{
                  borderColor: selected.includes(s.id) ? "hsl(var(--accent))" : undefined,
                  background: selected.includes(s.id) ? "hsl(220 100% 50% / 0.03)" : undefined,
                }}
                onClick={() => toggleSelect(s.id)}
                data-testid={`compare-select-${s.id}`}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Checkbox checked={selected.includes(s.id)} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "Manrope", fontWeight: 600, fontSize: 14 }}>{s.project_name || "Untitled"}</div>
                    <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>{s.location || "No location"}</div>
                  </div>
                  <button
                    onClick={(e) => handleDelete(s.id, e)}
                    style={{ padding: 4, cursor: "pointer", background: "none", border: "none" }}
                    data-testid={`delete-scenario-${s.id}`}
                  >
                    <Trash2 size={14} style={{ color: "hsl(var(--muted-foreground))" }} />
                  </button>
                </div>
                <div className="data-cell" style={{ marginTop: 8, fontSize: 12 }}>
                  GDV: {fmt(s.results?.kpis?.total_gdv)} &middot; Profit: {fmt(s.results?.kpis?.dev_profit)}
                </div>
              </div>
            ))}
          </div>
          {scenarios.length === 0 && (
            <div style={{ textAlign: "center", padding: 40, color: "hsl(var(--muted-foreground))" }}>
              No saved scenarios. Create appraisals first.
            </div>
          )}
          <Button
            onClick={runCompare}
            disabled={selected.length < 2 || loading}
            style={{ background: "hsl(var(--foreground))", color: "white" }}
            data-testid="run-compare-btn"
          >
            <GitCompare size={16} style={{ marginRight: 8 }} />
            Compare ({selected.length} selected)
          </Button>
        </>
      ) : (
        <>
          <Button
            variant="outline"
            onClick={handleBack}
            style={{ marginBottom: 20 }}
            data-testid="back-to-select-btn"
          >
            <X size={16} style={{ marginRight: 8 }} />
            Back to selection
          </Button>

          <Tabs defaultValue="metrics" data-testid="compare-tabs">
            <TabsList>
              <TabsTrigger value="metrics" data-testid="tab-metrics">
                <TrendingUp size={14} style={{ marginRight: 6 }} />Key Metrics
              </TabsTrigger>
              <TabsTrigger value="cashflow-overlay" data-testid="tab-cashflow-overlay" onClick={loadCashflowOverlay}>
                <BarChart3 size={14} style={{ marginRight: 6 }} />Cashflow Overlay
              </TabsTrigger>
            </TabsList>

            <TabsContent value="metrics" style={{ marginTop: 20 }}>
              <div style={{ overflowX: "auto" }}>
                <Table data-testid="comparison-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead style={{ minWidth: 180, fontWeight: 600, position: "sticky", left: 0, background: "white", zIndex: 2 }}>
                        Metric
                      </TableHead>
                      {comparison.map((s) => (
                        <TableHead key={s.id} style={{ minWidth: 160, textAlign: "right" }}>
                          <div style={{ fontFamily: "Manrope", fontWeight: 600, fontSize: 13 }}>{s.project_name || "Untitled"}</div>
                          <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", fontWeight: 400 }}>{s.location}</div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {metrics.map((m) => (
                      <TableRow key={m.label}>
                        <TableCell style={{ fontWeight: 500, position: "sticky", left: 0, background: "white", zIndex: 1 }}>
                          {m.label}
                        </TableCell>
                        {comparison.map((s) => (
                          <TableCell key={s.id} className="data-cell" style={{ textAlign: "right" }}>
                            {m.get(s)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="cashflow-overlay" style={{ marginTop: 20 }}>
              {cfLoading && (
                <div style={{ textAlign: "center", padding: 40, color: "hsl(var(--muted-foreground))" }}>Loading cashflow data...</div>
              )}
              {cashflowComparison && !cfLoading && (
                <div style={{ display: "grid", gap: 16 }} className="animate-in">
                  {/* Summary comparison cards */}
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(${cashflowComparison.length}, 1fr)`, gap: 12 }}>
                    {cashflowComparison.map((s, i) => (
                      <Card key={s.id} style={{ borderTop: `3px solid ${COLORS[i % COLORS.length]}` }}>
                        <CardContent style={{ padding: 16 }}>
                          <div style={{ fontFamily: "Manrope", fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{s.name}</div>
                          <div style={{ display: "grid", gap: 4, fontSize: 13 }}>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "hsl(var(--muted-foreground))" }}>Duration</span>
                              <span className="font-mono" style={{ fontWeight: 500 }}>{s.summary?.total_months} months</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "hsl(var(--muted-foreground))" }}>Peak Debt</span>
                              <span className="font-mono" style={{ fontWeight: 500 }}>{fmt(s.summary?.peak_debt)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "hsl(var(--muted-foreground))" }}>Peak -ve CF</span>
                              <span className="font-mono" style={{ fontWeight: 500 }}>{fmt(s.summary?.peak_negative_cashflow)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "hsl(var(--muted-foreground))" }}>Total Interest</span>
                              <span className="font-mono" style={{ fontWeight: 500 }}>{fmt(s.summary?.total_interest)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "hsl(var(--muted-foreground))" }}>Breakeven</span>
                              <span className="font-mono" style={{ fontWeight: 500 }}>
                                {s.summary?.breakeven_month ? `Month ${s.summary.breakeven_month}` : "N/A"}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Cumulative Cashflow Overlay */}
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                        <TrendingUp size={16} style={{ color: "hsl(var(--accent))" }} />
                        Cumulative Cashflow Overlay
                      </CardTitle>
                    </CardHeader>
                    <CardContent data-testid="cashflow-overlay-chart">
                      <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={buildOverlayData()} margin={{ top: 10, right: 20, left: 20, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis
                            dataKey="month"
                            tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}
                            tickFormatter={(v) => (v % 3 === 0 ? `M${v}` : "")}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}
                            tickFormatter={(v) => {
                              if (Math.abs(v) >= 1e6) return `\u00A3${(v / 1e6).toFixed(0)}m`;
                              if (Math.abs(v) >= 1e3) return `\u00A3${(v / 1e3).toFixed(0)}k`;
                              return `\u00A3${v}`;
                            }}
                          />
                          <Tooltip
                            contentStyle={{
                              fontFamily: "IBM Plex Sans", fontSize: 12,
                              borderRadius: 6, border: "1px solid hsl(var(--border))",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
                            }}
                            formatter={(value, name) => {
                              const idx = parseInt(name.split("_")[1]);
                              const prefix = name.startsWith("cum") ? "Cumulative" : "Debt";
                              const sName = cashflowComparison[idx]?.name || `Scenario ${idx + 1}`;
                              return [fmtFull(value), `${sName} - ${prefix}`];
                            }}
                            labelFormatter={(v) => `Month ${v}`}
                          />
                          <Legend
                            wrapperStyle={{ fontSize: 12, fontFamily: "IBM Plex Sans" }}
                            formatter={(value) => {
                              const idx = parseInt(value.split("_")[1]);
                              const prefix = value.startsWith("cum") ? "" : "(Debt) ";
                              return `${prefix}${cashflowComparison[idx]?.name || `Scenario ${idx + 1}`}`;
                            }}
                          />
                          <ReferenceLine y={0} stroke="hsl(var(--foreground))" strokeWidth={1.5} strokeOpacity={0.3} />
                          {cashflowComparison.map((s, i) => (
                            <Line
                              key={`cum_${i}`}
                              dataKey={`cum_${i}`}
                              type="monotone"
                              stroke={COLORS[i % COLORS.length]}
                              strokeWidth={2.5}
                              dot={false}
                              name={`cum_${i}`}
                            />
                          ))}
                          {cashflowComparison.map((s, i) => (
                            <Line
                              key={`debt_${i}`}
                              dataKey={`debt_${i}`}
                              type="monotone"
                              stroke={COLORS[i % COLORS.length]}
                              strokeWidth={1.5}
                              strokeDasharray="6 3"
                              dot={false}
                              name={`debt_${i}`}
                              strokeOpacity={0.5}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                      <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: 8, display: "flex", gap: 24 }}>
                        <span>Solid lines = Cumulative cashflow</span>
                        <span>Dashed lines = Debt balance</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Debt & Equity Comparison */}
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                        <BarChart3 size={16} style={{ color: "hsl(var(--accent))" }} />
                        Funding Profile Comparison
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table data-testid="funding-comparison-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead style={{ minWidth: 200 }}>Metric</TableHead>
                            {cashflowComparison.map((s, i) => (
                              <TableHead key={s.id} style={{ textAlign: "right", minWidth: 140 }}>
                                <span style={{ color: COLORS[i % COLORS.length], fontWeight: 600 }}>
                                  {s.name}
                                </span>
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {[
                            { label: "Project Duration", get: (s) => `${s.summary?.total_months} months` },
                            { label: "Development Period", get: (s) => `${s.summary?.dev_months} months` },
                            { label: "Stabilisation Period", get: (s) => `${s.summary?.stab_months} months` },
                            { label: "Peak Debt", get: (s) => fmtFull(s.summary?.peak_debt) },
                            { label: "Peak Negative Cashflow", get: (s) => fmtFull(s.summary?.peak_negative_cashflow) },
                            { label: "Total Interest Paid", get: (s) => fmtFull(s.summary?.total_interest) },
                            { label: "Total Rental Income", get: (s) => fmtFull(s.summary?.total_rental_income) },
                            { label: "Exit NDV", get: (s) => fmtFull(s.summary?.exit_ndv) },
                            { label: "Breakeven Month", get: (s) => s.summary?.breakeven_month ? `Month ${s.summary.breakeven_month}` : "N/A" },
                            { label: "Final Cumulative", get: (s) => fmtFull(s.summary?.final_cumulative) },
                          ].map((m) => (
                            <TableRow key={m.label}>
                              <TableCell style={{ fontWeight: 500 }}>{m.label}</TableCell>
                              {cashflowComparison.map((s) => (
                                <TableCell key={s.id} className="data-cell" style={{ textAlign: "right" }}>
                                  {m.get(s)}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
